import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../Icon";
import {
  healthCardPhotoSupported,
  type PreparedHealthCardPhoto,
  prepareHealthCardPhoto,
  scaledSize,
} from "../lib/healthCardPhoto";
import type { HealthCardReading } from "../lib/healthCardReader";
import { healthCardReadingSupported, readHealthCardPhoto } from "../lib/healthCardReaderEngine";

/**
 * What became of a photo the page sent (PA-13).
 *
 * - sent: the clinic has it (it checks it for safety before staff see it).
 * - not_sent: no answer, or a refusal that may pass later: the patient may try again.
 * - unreadable: the clinic refused the bytes: another photo may work.
 * - unavailable: the clinic is not taking photos now: the buttons go for the rest of the session.
 * - limit: this link sent the most photos it may: the buttons go too.
 */
export type HealthCardPhotoSendOutcome =
  | "sent"
  | "not_sent"
  | "unreadable"
  | "unavailable"
  | "limit";

/** Sends a prepared photo to the clinic. Never throws: every failure is an outcome. */
export type SendHealthCardPhoto = (jpeg: Uint8Array) => Promise<HealthCardPhotoSendOutcome>;

/** The photo row's state, held by the questionnaire so Back and Continue keep it. */
export type HealthCardPhotoState =
  | { readonly status: "none" | "preparing" | "unreadable" | "unavailable" | "limit" }
  | {
      readonly status: "sending" | "sent" | "not_sent";
      readonly photo: PreparedHealthCardPhoto;
    };

/**
 * Reading the card on the phone (owner decision, 2026-10-10), shown under the photo's status:
 * - idle: nothing to say (no reading, or it no longer applies);
 * - reading: the phone is reading the photo;
 * - filled: empty coverage answers were filled in from the card;
 * - checked: the number the patient typed is the card's, and nothing else was empty to fill;
 * - differs: the patient typed a number that is not the card's, so nothing was filled;
 * - unread: no health number could be read (or not in time). `numberGiven` says the patient
 *   had already typed one.
 * `birthDateDiffers` says the card's birth date is not the one the patient gave.
 */
export type HealthCardReadingState =
  | { readonly status: "idle" | "reading" | "differs" }
  | { readonly status: "unread"; readonly numberGiven: boolean }
  | { readonly status: "filled" | "checked"; readonly birthDateDiffers: boolean };

/** What the questionnaire did with a reading. */
export interface HealthCardReadingApplied {
  /** At least one empty answer was filled in from the card. */
  readonly filled: boolean;
  /** The patient typed a number that is not the card's: nothing was filled. */
  readonly numberDiffers: boolean;
  /** The card's birth date is not the date of birth the patient gave. */
  readonly birthDateDiffers: boolean;
}

/** How the questionnaire takes part in reading the card: its answers as of the last render. */
export interface HealthCardReadingOptions {
  /**
   * An Ontario card is chosen. Reading is for it alone: choosing another card stops a reading
   * under way and clears what the row says about reading.
   */
  readonly ontarioCard: boolean;
  /** The patient is on Coverage, where a reading shows what it filled in. Leaving stops one. */
  readonly onCoverage: boolean;
  /** The health number the patient typed, as typed. A note about it shows only beside it. */
  readonly givenNumber: string;
  /** The date of birth the patient gave. The note about the card's shows only beside this one. */
  readonly givenBirthDate: string;
  /** Fills the empty coverage answers from what was read, and says what it did. */
  readonly apply: (reading: HealthCardReading) => HealthCardReadingApplied;
}

export interface HealthCardPhotoControl {
  readonly state: HealthCardPhotoState;
  /** Reading the card on the phone; idle unless the questionnaire asked for it. */
  readonly reading: HealthCardReadingState;
  /** A photo taken now is read on the phone: an Ontario card, in a browser that can read. */
  readonly reads: boolean;
  /** Prepares and sends a picture the patient took or chose; replaces any earlier one. */
  readonly choose: (file: File) => void;
  /** Sends the photo on screen again after it was not sent. */
  readonly retry: () => void;
}

/**
 * The hook's own record of a reading. A note keeps the answer it was checked against (the typed
 * number, the date of birth), so it goes when the patient corrects that answer, and comes back
 * if they undo it.
 */
type ReadingRecord =
  | { readonly status: "idle" | "reading" }
  | { readonly status: "unread"; readonly numberGiven: boolean }
  | { readonly status: "differs"; readonly typedNumber: string }
  | { readonly status: "filled" | "checked"; readonly differingBirthDate: string | null };

const NONE: HealthCardPhotoState = { status: "none" };
const NOT_READING: ReadingRecord = { status: "idle" };
/** Finished readings whose line describes answers still on the form. */
const FILLED_LINES: readonly HealthCardReadingState["status"][] = ["filled", "checked", "differs"];
/** Every finished reading's line. */
const FINISHED_LINES: readonly HealthCardReadingState["status"][] = [...FILLED_LINES, "unread"];

/**
 * The questionnaire's health card photo: prepared and sent in the background, so Continue and
 * Submit never wait for it. `control` is undefined when there is nothing to send with (the demo
 * lane) or the browser cannot prepare a photo; the row is then not shown at all. `reset` drops
 * the photo and its state, as when the session ends.
 *
 * With `reading`, a prepared photo of an Ontario card is also read on the phone, beside its
 * upload and never in its way: the upload starts first, the reading has its own state and time
 * limit, and what it read is used only once the clinic took the photo (or the upload failed
 * without a refusal). It stops with a new photo, a photo the clinic refused or will not take
 * (photos are off), another kind of card, leaving Coverage, the end of the session, or leaving
 * the page.
 */
export function useHealthCardPhoto(
  send: SendHealthCardPhoto | undefined,
  reading?: HealthCardReadingOptions,
): {
  readonly control: HealthCardPhotoControl | undefined;
  readonly reset: () => void;
} {
  const [supported] = useState(healthCardPhotoSupported);
  const [readingSupported] = useState(healthCardReadingSupported);
  const [state, setState] = useState<HealthCardPhotoState>(NONE);
  const [record, setRecord] = useState<ReadingRecord>(NOT_READING);
  // Each choice, retry and reset gets a new number; a late answer for an older one is dropped.
  const attempt = useRef(0);
  const shown = useRef<PreparedHealthCardPhoto | null>(null);
  const sendRef = useRef(send);
  const readingRef = useRef(reading);
  // The reading under way, if any: aborting it stops the reader and terminates its worker.
  const readRun = useRef<AbortController | null>(null);
  useEffect(() => {
    sendRef.current = send;
  }, [send]);
  useEffect(() => {
    readingRef.current = reading;
  }, [reading]);

  /**
   * Stops a reading under way and clears what the row says about reading, except a finished
   * reading's line listed in `keep`.
   */
  const stopReading = useCallback((keep: readonly HealthCardReadingState["status"][] = []) => {
    readRun.current?.abort();
    readRun.current = null;
    setRecord((current) => (keep.includes(current.status) ? current : NOT_READING));
  }, []);

  const ontarioCard = reading?.ontarioCard === true;
  const onCoverage = reading?.onCoverage === true;
  useEffect(() => {
    // Another kind of card: nothing on the row is about reading any more.
    if (!ontarioCard) stopReading();
    // Off Coverage: a reading under way stops (its worker too); a finished line stays for Back.
    else if (!onCoverage) stopReading(FINISHED_LINES);
  }, [ontarioCard, onCoverage, stopReading]);

  const read = useCallback(
    async (
      photo: PreparedHealthCardPhoto,
      delivering: Promise<HealthCardPhotoSendOutcome | null>,
    ) => {
      const options = readingRef.current;
      if (!readingSupported || options?.ontarioCard !== true || !options.onCoverage) return;
      const run = new AbortController();
      readRun.current = run;
      setRecord({ status: "reading" });
      const [found, taken] = await Promise.all([
        readHealthCardPhoto(photo.preview, { signal: run.signal }).catch(() => null),
        // A photo the clinic refused, or will not take because photos are off, is not read
        // either: what was read waits for the upload's answer.
        delivering.then(
          (outcome) => outcome === "sent" || outcome === "not_sent",
          () => false,
        ),
      ]);
      // A newer photo, a refusal, a reset or leaving the step stopped this one meanwhile.
      if (readRun.current !== run) return;
      readRun.current = null;
      const latest = readingRef.current;
      if (!taken || latest === undefined || !latest.ontarioCard || !latest.onCoverage) {
        setRecord(NOT_READING);
        return;
      }
      if (found === null) {
        setRecord({ status: "unread", numberGiven: latest.givenNumber.trim() !== "" });
        return;
      }
      const applied = latest.apply(found);
      setRecord(
        applied.numberDiffers
          ? { status: "differs", typedNumber: latest.givenNumber }
          : {
              status: applied.filled ? "filled" : "checked",
              differingBirthDate: applied.birthDateDiffers ? latest.givenBirthDate : null,
            },
      );
    },
    [readingSupported],
  );

  /** Forgets the photo on screen and frees its preview. */
  const release = useCallback(() => {
    shown.current?.preview.close();
    shown.current = null;
  }, []);

  /** Sends the photo; resolves to the clinic's answer, or null when a newer attempt took over. */
  const deliver = useCallback(
    async (photo: PreparedHealthCardPhoto, id: number) => {
      const sender = sendRef.current;
      if (sender === undefined) return null;
      setState({ status: "sending", photo });
      const outcome = await sender(photo.jpeg).catch((): HealthCardPhotoSendOutcome => "not_sent");
      if (id !== attempt.current) return null;
      if (outcome === "sent" || outcome === "not_sent") {
        setState({ status: outcome, photo });
        return outcome;
      }
      // Refused, or the clinic is not taking photos: this photo is not read either. A line
      // about answers already filled in (after an earlier failed upload) stays beside them.
      stopReading(FILLED_LINES);
      release();
      setState({ status: outcome });
      return outcome;
    },
    [release, stopReading],
  );

  const choose = useCallback(
    (file: File) => {
      attempt.current += 1;
      const id = attempt.current;
      stopReading();
      release();
      setState({ status: "preparing" });
      void prepareHealthCardPhoto(file).then(
        (photo) => {
          if (id !== attempt.current) {
            photo.preview.close();
            return;
          }
          shown.current = photo;
          // The upload starts first, exactly as before; the reading goes beside it.
          const delivering = deliver(photo, id);
          void read(photo, delivering);
          return delivering;
        },
        () => {
          if (id === attempt.current) setState({ status: "unreadable" });
        },
      );
    },
    [deliver, read, release, stopReading],
  );

  const retry = useCallback(() => {
    const photo = shown.current;
    if (photo === null) return;
    attempt.current += 1;
    void deliver(photo, attempt.current);
  }, [deliver]);

  const reset = useCallback(() => {
    attempt.current += 1;
    stopReading();
    release();
    setState(NONE);
  }, [release, stopReading]);

  useEffect(
    () => () => {
      // Preparing can finish after the questionnaire leaves. Do not send that photo or
      // retain its new preview after the session has gone; stop any reading too.
      attempt.current += 1;
      readRun.current?.abort();
      readRun.current = null;
      release();
    },
    [release],
  );

  const givenNumber = reading?.givenNumber;
  const givenBirthDate = reading?.givenBirthDate;
  const shownReading = useMemo<HealthCardReadingState>(() => {
    switch (record.status) {
      case "differs":
        return record.typedNumber === givenNumber ? { status: "differs" } : { status: "idle" };
      case "filled":
      case "checked":
        return {
          status: record.status,
          birthDateDiffers:
            record.differingBirthDate !== null && record.differingBirthDate === givenBirthDate,
        };
      default:
        return record;
    }
  }, [record, givenNumber, givenBirthDate]);
  const reads = readingSupported && ontarioCard;
  const control = useMemo<HealthCardPhotoControl | undefined>(
    () =>
      send === undefined || !supported
        ? undefined
        : { state, reading: shownReading, reads, choose, retry },
    [send, supported, state, shownReading, reads, choose, retry],
  );
  return { control, reset };
}

/** The preview's long edge in canvas pixels: sharp on any phone's screen at the row's size. */
const PREVIEW_LONG_EDGE_PX = 640;

function HealthCardPhotoPreview({ bitmap }: { readonly bitmap: ImageBitmap }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    const size = scaledSize(bitmap.width, bitmap.height, PREVIEW_LONG_EDGE_PX);
    canvas.width = size.width;
    canvas.height = size.height;
    try {
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0, size.width, size.height);
    } catch {
      // A preview freed meanwhile (the photo was replaced) draws nothing.
    }
  }, [bitmap]);
  return (
    <canvas
      ref={canvasRef}
      className="health-card-photo-preview"
      role="img"
      aria-label="Photo of your health card"
    />
  );
}

const statusText: Record<HealthCardPhotoState["status"], string> = {
  none: "",
  preparing: "Preparing your photo…",
  sending: "Sending your photo…",
  sent: "Photo sent",
  not_sent: "Photo not sent. You can type the details instead.",
  unreadable: "This photo couldn't be used. Take it again in good light, or type the details.",
  unavailable: "Photos can't be sent right now. Type the details below.",
  limit: "No more photos can be sent from this link. Type the details below.",
};

/**
 * The reading's line. While the photo is still preparing or sending, "Reading" waits so only
 * one spinner shows; after a failed upload, whose line already says to type, an unread card
 * adds nothing.
 */
function readingLine(reading: HealthCardReadingState, busy: boolean, problem: boolean): string {
  switch (reading.status) {
    case "reading":
      return busy ? "" : "Reading your card…";
    case "filled":
      return "Filled in from your card. Check it matches.";
    case "checked":
      return "The number matches your card.";
    case "unread":
      if (problem) return "";
      return reading.numberGiven
        ? "Couldn't read the card."
        : "Couldn't read the card. Type the number below.";
    default:
      return "";
  }
}

/** A reading's one warning: the number typed, or else the birth date, is not the card's. */
function readingNote(reading: HealthCardReadingState): string | null {
  if (reading.status === "differs") {
    return "This card's number doesn't match what you typed. Check the number below.";
  }
  if ("birthDateDiffers" in reading && reading.birthDateDiffers) {
    return "This card's birth date doesn't match yours. Check it's your card.";
  }
  return null;
}

/**
 * What reading the card came to, under the photo's status. A live region of its own (not a
 * second role=status), mounted before it speaks so screen readers hear each change. It never
 * repeats a number or date from the card.
 */
function HealthCardReadingLines({
  reading,
  busy,
  problem,
}: {
  readonly reading: HealthCardReadingState;
  readonly busy: boolean;
  readonly problem: boolean;
}) {
  const line = readingLine(reading, busy, problem);
  const note = readingNote(reading);
  return (
    <div className="health-card-photo-reading" aria-live="polite">
      {line === "" ? null : (
        <p>
          {reading.status === "reading" ? (
            <span className="health-card-photo-spinner" aria-hidden="true" />
          ) : null}
          {line}
        </p>
      )}
      {note === null ? null : (
        <p className="health-card-photo-reading-note">
          <Icon name="warning" />
          {note}
        </p>
      )}
    </div>
  );
}

/**
 * The optional photo of the front of the card, under the coverage choice: a compact card with
 * the title, a one-line hint (the number is still typed; or, for an Ontario card read on the
 * phone, filled in to check), a small preview and the status once there is a photo, and small
 * secondary buttons. Its states stay on the row: Preparing, Sending, Photo sent (Retake), Photo
 * not sent (Try again), and a line in place of the buttons when the clinic is not taking photos.
 * The number fields stay required. Anything else about the photo (such as what was read from
 * it) belongs in `.health-card-photo-result`, beside the preview and under the status.
 */
export function HealthCardPhotoRow({ control }: { readonly control: HealthCardPhotoControl }) {
  const takeInput = useRef<HTMLInputElement>(null);
  const chooseInput = useRef<HTMLInputElement>(null);
  const { state } = control;
  const photo = "photo" in state ? state.photo : undefined;
  const busy = state.status === "preparing" || state.status === "sending";
  const closed = state.status === "unavailable" || state.status === "limit";
  const problem = state.status === "not_sent" || state.status === "unreadable";
  const tone = state.status === "sent" ? " sent" : problem ? " problem" : "";
  const picked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so the same picture can be chosen again.
    event.target.value = "";
    if (file !== undefined) control.choose(file);
  };
  return (
    <fieldset className="health-card-photo">
      <legend className="health-card-photo-title">
        <span className="health-card-photo-mark">
          <Icon name="camera" />
        </span>
        Add a photo of your card
      </legend>
      {/* Stays with the photo: a photo never stands in for the number. The closed lines already
          say to type the details. */}
      {closed ? null : (
        <p className="health-card-photo-hint">
          {control.reads
            ? "Optional, front only. Sent to the clinic now; we fill in the number for you to check."
            : "Optional, front only. Sent to the clinic now; you still type the number below."}
        </p>
      )}
      <div className="health-card-photo-result">
        {photo === undefined ? null : <HealthCardPhotoPreview bitmap={photo.preview} />}
        <div className="health-card-photo-lines">
          <p className={`health-card-photo-status${tone}`} role="status">
            {busy ? <span className="health-card-photo-spinner" aria-hidden="true" /> : null}
            {state.status === "sent" ? <Icon name="check" /> : null}
            {problem ? <Icon name="warning" /> : null}
            {statusText[state.status]}
          </p>
          <HealthCardReadingLines reading={control.reading} busy={busy} problem={problem} />
        </div>
      </div>
      {busy || closed ? null : (
        <div className="health-card-photo-actions">
          {state.status === "not_sent" ? (
            <button type="button" className="outline-button" onClick={control.retry}>
              Try again
            </button>
          ) : null}
          <button
            type="button"
            className="outline-button"
            onClick={() => takeInput.current?.click()}
          >
            <Icon name="camera" />
            {photo === undefined ? "Take a photo of your card" : "Retake photo"}
          </button>
          {photo === undefined ? (
            <button
              type="button"
              className="outline-button"
              onClick={() => chooseInput.current?.click()}
            >
              Choose a saved photo
            </button>
          ) : null}
        </div>
      )}
      <input
        ref={takeInput}
        className="health-card-photo-input"
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        tabIndex={-1}
        onChange={picked}
      />
      <input
        ref={chooseInput}
        className="health-card-photo-input"
        type="file"
        accept="image/*"
        hidden
        tabIndex={-1}
        onChange={picked}
      />
    </fieldset>
  );
}
